import type {
  DrinkItem,
  DrinkTagPrice,
  EventSegment,
  FoodItem,
  FoodTagPrice,
  Ingredient,
  InfraItem,
  InfraTagPrice,
  ResolvedPrice,
  ScenarioTag,
} from "./types";

// ─── Tag price resolution ─────────────────────────────────────────────────────

type TagPriceEntry = InfraTagPrice | FoodTagPrice | DrinkTagPrice;
type PriceField = "amount" | "pricePerPerson" | "manualPrice";

export function resolveTagPrice(
  tagPrices: TagPriceEntry[],
  activeTagId: string | null,
  allTags: ScenarioTag[],
  field: PriceField
): ResolvedPrice {
  if (!tagPrices || tagPrices.length === 0) {
    return { value: 0, resolvedTagId: null, isExact: false, isFallback: false, isEmpty: true };
  }

  const activeTag = allTags.find((t) => t.id === activeTagId);

  // Try exact match first
  const exactEntry = tagPrices.find((tp) => tp.tagId === activeTagId);
  if (exactEntry) {
    return {
      value: (exactEntry as any)[field] ?? 0,
      resolvedTagId: exactEntry.tagId,
      isExact: true,
      isFallback: false,
      isEmpty: false,
    };
  }

  // Fall back to nearest by order
  if (!activeTag) {
    // No active tag set — use first defined entry
    const first = tagPrices[0];
    return {
      value: (first as any)[field] ?? 0,
      resolvedTagId: first.tagId,
      isExact: false,
      isFallback: true,
      isEmpty: false,
    };
  }

  let nearest: TagPriceEntry | null = null;
  let nearestDistance = Infinity;

  for (const entry of tagPrices) {
    const entryTag = allTags.find((t) => t.id === entry.tagId);
    if (!entryTag) continue;
    const distance = Math.abs(activeTag.order - entryTag.order);
    if (
      distance < nearestDistance ||
      (distance === nearestDistance && entryTag.order < (allTags.find((t) => t.id === nearest?.tagId)?.order ?? Infinity))
    ) {
      nearestDistance = distance;
      nearest = entry;
    }
  }

  if (nearest) {
    return {
      value: (nearest as any)[field] ?? 0,
      resolvedTagId: nearest.tagId,
      isExact: false,
      isFallback: true,
      isEmpty: false,
    };
  }

  return { value: 0, resolvedTagId: null, isExact: false, isFallback: false, isEmpty: true };
}

// ─── Ingredient price ─────────────────────────────────────────────────────────

export function computeIngredientPrice(ingredient: Ingredient): number {
  const packageSize = ingredient.packageSize > 0 ? ingredient.packageSize : 1;
  return (ingredient.quantity / packageSize) * ingredient.packagePrice;
}

export function computeRecipePrice(ingredients: Ingredient[]): number | null {
  const active = ingredients.filter((i) => i.active);
  if (active.length === 0) return null;
  return active.reduce((sum, i) => sum + computeIngredientPrice(i), 0);
}

// ─── Row costs ────────────────────────────────────────────────────────────────

export function computeInfraRowCost(
  item: InfraItem,
  activeTagId: string | null,
  allTags: ScenarioTag[]
): { cost: number; resolved: ResolvedPrice } {
  const resolved = resolveTagPrice(item.tagPrices, activeTagId, allTags, "amount");
  return { cost: item.active ? resolved.value : 0, resolved };
}

export function computeFoodRowCost(
  item: FoodItem,
  activeTagId: string | null,
  allTags: ScenarioTag[],
  allSegments: EventSegment[]
): { cost: number; resolved: ResolvedPrice; persons: number } {
  const resolved = resolveTagPrice(item.tagPrices, activeTagId, allTags, "pricePerPerson");
  const persons =
    item.eventSegmentIds.length > 0
      ? allSegments
          .filter((s) => item.eventSegmentIds.includes(s.id))
          .reduce((sum, s) => sum + s.attendeeCount, 0)
      : item.persons;
  const cost = item.active ? resolved.value * persons : 0;
  return { cost, resolved, persons };
}

export function computeDrinkRowCost(
  item: DrinkItem,
  activeTagId: string | null,
  allTags: ScenarioTag[],
  allSegments: EventSegment[],
  ingredients: Ingredient[],
  zusagenPercent: number
): { cost: number; resolved: ResolvedPrice; effectivePrice: number; personCount: number } {
  const recipePrice = computeRecipePrice(ingredients);
  const resolved = resolveTagPrice(item.tagPrices, activeTagId, allTags, "manualPrice");

  const effectivePrice = recipePrice !== null ? recipePrice : resolved.value;

  const segmentPersons = allSegments
    .filter((s) => item.eventSegmentIds.includes(s.id))
    .reduce((sum, s) => sum + s.attendeeCount, 0);
  const personCount = segmentPersons * (zusagenPercent / 100);

  const cost = item.active ? item.servingsPerPerson * effectivePrice * personCount : 0;

  return {
    cost,
    resolved: recipePrice !== null
      ? { value: recipePrice, resolvedTagId: null, isExact: true, isFallback: false, isEmpty: false }
      : resolved,
    effectivePrice,
    personCount,
  };
}

// ─── Section totals ───────────────────────────────────────────────────────────

export function computeInfraTotal(
  items: InfraItem[],
  activeTagId: string | null,
  allTags: ScenarioTag[]
): number {
  return items.reduce((sum, item) => sum + computeInfraRowCost(item, activeTagId, allTags).cost, 0);
}

export function computeFoodTotal(
  items: FoodItem[],
  activeTagId: string | null,
  allTags: ScenarioTag[],
  allSegments: EventSegment[]
): number {
  return items.reduce(
    (sum, item) => sum + computeFoodRowCost(item, activeTagId, allTags, allSegments).cost,
    0
  );
}

export function computeDrinksTotal(
  items: DrinkItem[],
  ingredientsByDrink: Map<string, Ingredient[]>,
  activeTagId: string | null,
  allTags: ScenarioTag[],
  allSegments: EventSegment[],
  zusagenPercent: number
): number {
  return items.reduce((sum, item) => {
    const ingredients = item.objectId ? (ingredientsByDrink.get(item.objectId) ?? []) : [];
    return sum + computeDrinkRowCost(item, activeTagId, allTags, allSegments, ingredients, zusagenPercent).cost;
  }, 0);
}

export function computeDrinksTotalByType(
  items: DrinkItem[],
  ingredientsByDrink: Map<string, Ingredient[]>,
  activeTagId: string | null,
  allTags: ScenarioTag[],
  allSegments: EventSegment[],
  zusagenPercent: number
): Record<string, number> {
  const result: Record<string, number> = {};
  for (const item of items) {
    const ingredients = item.objectId ? (ingredientsByDrink.get(item.objectId) ?? []) : [];
    const { cost } = computeDrinkRowCost(item, activeTagId, allTags, allSegments, ingredients, zusagenPercent);
    const type = item.drinkType || "";
    result[type] = (result[type] ?? 0) + cost;
  }
  return result;
}

export function computeDrinksEffectiveTotal(
  byType: Record<string, number>,
  minimums: Record<string, number>
): { effectiveByType: Record<string, number>; total: number } {
  const effectiveByType: Record<string, number> = {};
  let total = 0;
  const allTypes = new Set([...Object.keys(byType), ...Object.keys(minimums)]);
  for (const type of allTypes) {
    const calculated = byType[type] ?? 0;
    const minimum = minimums[type] ?? 0;
    const effective = Math.max(calculated, minimum);
    effectiveByType[type] = effective;
    total += effective;
  }
  return { effectiveByType, total };
}

export function computeGrandTotal(
  infraTotal: number,
  foodTotal: number,
  drinksTotal: number,
  aufbau: number,
  abbau: number
): number {
  return infraTotal + foodTotal + drinksTotal + aufbau + abbau;
}

export function computeActualTotal(
  infraItems: InfraItem[],
  foodItems: FoodItem[],
  drinkItems: DrinkItem[]
): number {
  const sum = (items: Array<InfraItem | FoodItem | DrinkItem>) =>
    items
      .filter((item) => item.active && item.isActual && item.actualCost !== null)
      .reduce((acc, item) => acc + (item.actualCost as number), 0);
  return sum(infraItems) + sum(foodItems) + sum(drinkItems);
}
